import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  FileText,
  Search,
  PieChart,
  Settings,
  Bell,
  ClipboardList,
  FileStack,
} from 'lucide-react';
import { useRoleAccess } from '@/features/auth/hooks/useRoleAccess';
import { ROLES } from '@/features/auth/constants/roles';
import { PRE_REGISTRATION_VIEW_ROLES, preRegistrationRoutes } from '@/features/pre-registrations';

type SidebarProps = {
  isCollapsed: boolean;
};

type NavItemProps = {
  to: string;
  icon: React.ReactNode;
  label: string;
};

const NavItem: React.FC<NavItemProps> = ({ to, icon, label }) => {
  const activeClass = "flex items-center space-x-2 px-3 py-2 bg-sidebar-accent text-sidebar-accent-foreground rounded-md";
  const inactiveClass = "flex items-center space-x-2 px-3 py-2 text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground rounded-md transition-colors";
  const getClass = ({ isActive }: { isActive: boolean }) => isActive ? activeClass : inactiveClass;

  return (
    <NavLink to={to} className={getClass}>
      {icon}
      <span>{label}</span>
    </NavLink>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed }) => {
  const { hasRole } = useRoleAccess();

  if (isCollapsed) return null;

  return (
    <div className="w-52 bg-sidebar-background border-r border-sidebar-border flex-shrink-0 h-full overflow-y-auto">
      <div className="p-3">
        <nav className="space-y-6">
          <div className="space-y-2">
            <h3 className="text-xs uppercase text-sidebar-foreground/60 font-medium tracking-wider px-1">
              Principal
            </h3>
            <div className="flex flex-col space-y-1">
              {hasRole([
                ROLES.ADMIN,
                ROLES.FISCALIZACION_EDITOR,
                ROLES.FISCALIZACION_LECTOR,
                ROLES.OBSERVATORIO_EDITOR,
                ROLES.OBSERVATORIO_LECTOR,
              ]) && (
                <NavItem to="/news" icon={<Bell className="w-5 h-5" />} label="Notificaciones" />
              )}

              {hasRole([ROLES.ADMIN, ROLES.FISCALIZACION_EDITOR]) && (
                <NavItem to="/accommodation" icon={<Building2 className="w-5 h-5" />} label="Nuevo Alojamiento" />
              )}

              {hasRole([
                ROLES.ADMIN,
                ROLES.FISCALIZACION_EDITOR,
                ROLES.FISCALIZACION_LECTOR,
                ROLES.OBSERVATORIO_EDITOR,
                ROLES.OBSERVATORIO_LECTOR,
                ROLES.INFORMES_LECTOR
              ]) && (
                <NavItem to="/buscador" icon={<Search className="w-5 h-5" />} label="Buscar" />
              )}

              {hasRole([
                ROLES.ADMIN,
                ROLES.FISCALIZACION_LECTOR,
                ROLES.FISCALIZACION_EDITOR,
                ROLES.OBSERVATORIO_EDITOR,
                ROLES.OBSERVATORIO_LECTOR,
                ROLES.INFORMES_LECTOR
              ]) && (
                <NavItem to="/estadisticas" icon={<LayoutDashboard className="w-5 h-5" />} label="Dashboard" />
              )}
            </div>
          </div>

          {hasRole(PRE_REGISTRATION_VIEW_ROLES) && (
            <div className="space-y-2">
              <h3 className="text-xs uppercase text-sidebar-foreground/60 font-medium tracking-wider px-1">
                Preinscripciones
              </h3>
              <div className="flex flex-col space-y-1">
                <NavItem to={preRegistrationRoutes.accommodations} icon={<ClipboardList className="w-5 h-5" />} label="Alojamientos" />
                <NavItem to={preRegistrationRoutes.municipalLists} icon={<FileStack className="w-5 h-5" />} label="Listados municipales" />
              </div>
            </div>
          )}

          {hasRole([
            ROLES.ADMIN,
            ROLES.OBSERVATORIO_EDITOR,
            ROLES.OBSERVATORIO_LECTOR,
            ROLES.OBSERVATORIO_OBSERVADOR
          ]) && (
            <div className="space-y-2">
              <h3 className="text-xs uppercase text-sidebar-foreground/60 font-medium tracking-wider px-1">
                Muestras
              </h3>
              <div className="flex flex-col space-y-1">
                {hasRole([ROLES.ADMIN, ROLES.OBSERVATORIO_EDITOR]) && (
                  <NavItem to="/muestras/asignar" icon={<Settings className="w-5 h-5" />} label="Configurar" />
                )}

                {hasRole([ROLES.ADMIN, ROLES.OBSERVATORIO_EDITOR]) && (
                  <NavItem to="/muestras/crear" icon={<PieChart className="w-5 h-5" />} label="Generar" />
                )}

                {hasRole([ROLES.ADMIN, ROLES.OBSERVATORIO_EDITOR, ROLES.OBSERVATORIO_LECTOR]) && (
                  <NavItem to="/muestras/buscar" icon={<Search className="w-5 h-5" />} label="Buscar" />
                )}

                {hasRole([ROLES.ADMIN, ROLES.OBSERVATORIO_OBSERVADOR, ROLES.OBSERVATORIO_LECTOR, ROLES.OBSERVATORIO_EDITOR]) && (
                  <NavItem to="/muestras/completar" icon={<FileText className="w-5 h-5" />} label="Completar" />
                )}
              </div>
            </div>
          )}
        </nav>
      </div>
    </div>
  );
};

export default Sidebar;
